import { Badge } from '@/components/ui/badge';
import { LabeledSection } from '@/components/ui/labeled-section';
import { formatDateTime } from '@/lib/datetime';
import { formatMoneyCompact } from '@/lib/money';
import { INSIGHT_KEYS as K, insightReader, type Insights } from '../insights';

/**
 * The channel's proposal insights (Upwork: what the client did with the field
 * of proposals, and what the competing applicants look like on average), laid
 * out as three readable groups instead of raw JSON. Every key is optional; any
 * key not shown here (unknown, unparsable, or a shadowed alias) still appears
 * as a plain row under "More".
 */

const oneDecimal = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));
/** Job Success Score comes as a fraction (0.99) or a percentage (99). */
const jssPct = (n: number) => `${Math.round(n <= 1 ? n * 100 : n)}%`;

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-muted/50 rounded-md px-3 py-2">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="text-sm font-medium tabular-nums">{value}</p>
    </div>
  );
}

export function BidInsights({ value, currency }: { value: Insights; currency: string }) {
  const read = insightReader(value);
  const total = read.number(K.total);
  const opened = read.number(K.opened);
  const unopened = read.number(K.unopened);
  const openedOf = total ?? (opened !== null && unopened !== null ? opened + unopened : null);
  const shortlisted = read.number(K.shortlisted);
  const messaged = read.number(K.messaged);

  const jss = read.number(K.jss);
  const earnings = read.number(K.earnings);
  const jobs = read.number(K.jobs);
  const years = read.number(K.years);
  const competitors = [
    jss !== null ? { label: 'Job Success', value: jssPct(jss) } : null,
    // Upwork reports earnings in the channel's currency (USD for synced bids).
    earnings !== null ? { label: 'Earnings', value: formatMoneyCompact(earnings, currency) ?? '—' } : null,
    jobs !== null ? { label: 'Completed jobs', value: oneDecimal(jobs) } : null,
    years !== null ? { label: 'Years on Upwork', value: oneDecimal(years) } : null,
  ].filter((x): x is { label: string; value: string } => x !== null);

  const skills = read.skills(K.skills);
  const computedAt = read.text(K.computedAt);
  const rest = read.rest();

  const pctOpened = openedOf && opened !== null ? Math.min(100, Math.round((opened / openedOf) * 100)) : null;
  const hasActivity = openedOf !== null || opened !== null || shortlisted !== null || messaged !== null;

  return (
    <div className="space-y-5">
      {hasActivity ? (
        <LabeledSection title="Client activity">
          {opened !== null && openedOf !== null ? (
            <div className="space-y-1.5">
              <p className="text-sm">
                Opened <span className="font-medium tabular-nums">{opened}</span> of{' '}
                <span className="font-medium tabular-nums">{openedOf}</span> proposals
              </p>
              <div
                className="bg-muted h-1.5 overflow-hidden rounded-full"
                role="meter"
                aria-label="Share of proposals the client opened"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={pctOpened ?? 0}
              >
                <div className="bg-primary h-full rounded-full" style={{ width: `${pctOpened ?? 0}%` }} />
              </div>
            </div>
          ) : openedOf !== null ? (
            <p className="text-sm">
              <span className="font-medium tabular-nums">{openedOf}</span> proposals on the job
            </p>
          ) : opened !== null ? (
            <p className="text-sm">
              Opened <span className="font-medium tabular-nums">{opened}</span> proposals
            </p>
          ) : null}
          {shortlisted !== null || messaged !== null ? (
            <div className="grid grid-cols-2 gap-2">
              {shortlisted !== null ? <Stat label="Shortlisted" value={String(shortlisted)} /> : null}
              {messaged !== null ? <Stat label="Messaged" value={String(messaged)} /> : null}
            </div>
          ) : null}
        </LabeledSection>
      ) : null}

      {competitors.length > 0 ? (
        <LabeledSection title="Competing applicants · average">
          <div className="grid grid-cols-2 gap-2">
            {competitors.map((c) => (
              <Stat key={c.label} label={c.label} value={c.value} />
            ))}
          </div>
        </LabeledSection>
      ) : null}

      {skills.length > 0 ? (
        <LabeledSection title="Their most common skills">
          <ul className="flex flex-wrap gap-1.5" aria-label="Most common applicant skills">
            {skills.map((s) => (
              <li key={s.skill}>
                <Badge variant="secondary" className="gap-1.5 font-normal">
                  {s.skill}
                  {s.applicants !== null ? (
                    <span
                      className="text-muted-foreground tabular-nums"
                      aria-label={`${s.applicants} applicants`}
                    >
                      {s.applicants}
                    </span>
                  ) : null}
                </Badge>
              </li>
            ))}
          </ul>
        </LabeledSection>
      ) : null}

      {rest.length > 0 ? (
        <LabeledSection title="More">
          <dl className="grid gap-1.5 text-sm">
            {rest.map(([k, v]) => (
              <div key={k} className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
                <dt className="text-muted-foreground truncate">{k.replace(/_/g, ' ')}</dt>
                <dd className="text-right tabular-nums">
                  {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                </dd>
              </div>
            ))}
          </dl>
        </LabeledSection>
      ) : null}

      {computedAt ? (
        <p className="text-muted-foreground text-xs">Upwork computed these {formatDateTime(computedAt)}.</p>
      ) : null}
    </div>
  );
}
